

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { getAssistantIdForGender } from '@/lib/vapi-config';
import { supabase } from '@/lib/supabase-client';
import { calculateTokensForDuration, formatMinutesWithTokens } from '@/lib/token-utils';
import { useAuth } from '@/context/AuthContext';

interface InterviewConfig {
  id: string;
  name: string;
  interview_type: string;
  difficulty_level: string;
}

interface Question {
  question: string;
  expected_answer: string;
  keywords: string;
}

export default function CreateJobPage() {
  const navigate = useNavigate();
  const { organizationId, isRecruiterValue } = useAuth();
  const [loading, setLoading] = useState(false);
  const [configs, setConfigs] = useState<InterviewConfig[]>([]);
  const [useExistingConfig, setUseExistingConfig] = useState(true);
  const isSubmittingRef = useRef(false);
  const [formData, setFormData] = useState({
    title: '',
    company_name: '',
    description: '',
    requirements: '',
    tech_stack: '',
    location: '',
    salary_range: '',
    employment_type: 'full-time',
    interview_config_id: '',
    posted_by: ''
  });
  const [interviewConfig, setInterviewConfig] = useState({
    name: '',
    personality: '',
    interview_rules: '',
    interview_type: 'behavioral',
    difficulty_level: 'mid',
    max_duration_minutes: 10,
    max_questions: 5,
    voice_gender: 'male' as 'male' | 'female',
    randomize_questions: false
  });
  const [questions, setQuestions] = useState<Question[]>([
    { question: '', expected_answer: '', keywords: '' }
  ]);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const { data, error } = await supabase
        .from('interview_configurations')
        .select('id, name, interview_type, difficulty_level')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching configs:', error);
      } else {
        setConfigs(data || []);
      }
    } catch (error) {
      console.error('Error fetching configs:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmittingRef.current) {
      console.log('[JobCreate] Submission already in progress, ignoring duplicate submit');
      return;
    }

    if (useExistingConfig && !formData.interview_config_id) {
      alert('Please select an interview configuration');
      return;
    }

    console.log('[JobCreate] Starting job creation...');
    isSubmittingRef.current = true;
    setLoading(true);

    try {
      let configId = formData.interview_config_id;

      if (!useExistingConfig) {
        console.log('[JobCreate] Creating new interview configuration...');
        const questionsJson = questions
          .filter(q => q.question.trim())
          .map(q => ({
            question: q.question,
            expected_answer: q.expected_answer,
            keywords: q.keywords.split(',').map(k => k.trim()).filter(k => k)
          }));

        const configName = interviewConfig.name || `${formData.title} Interview`;
        const roleStatement = `You are an AI interviewer conducting an interview for a ${formData.title} position at ${formData.company_name}. ${formData.description}`;
        const assistantId = getAssistantIdForGender(interviewConfig.voice_gender);

        const { interviewConfigsApi } = await import('@/services/api');
        const configData = await interviewConfigsApi.create({
          name: configName,
          assistant_id: assistantId,
          role_statement: roleStatement,
          personality: interviewConfig.personality || 'Professional and encouraging',
          interview_rules: interviewConfig.interview_rules || 'Ask questions one at a time. Listen carefully to responses.',
          default_questions: { questions: questionsJson },
          difficulty_level: interviewConfig.difficulty_level,
          interview_type: interviewConfig.interview_type,
          max_duration_minutes: interviewConfig.max_duration_minutes,
          max_questions: interviewConfig.max_questions,
          voice_gender: interviewConfig.voice_gender,
          is_active: true
        });
        configId = configData.config.id;
        console.log('[JobCreate] Created interview configuration:', configId);
      }

      console.log('[JobCreate] Creating job posting...');
      const { jobsApi } = await import('@/services/api');
      const jobData: any = { ...formData, interview_config_id: configId };

      if (isRecruiterValue && organizationId) {
        jobData.organization_id = organizationId;
        console.log('[JobCreate] Adding organization_id:', organizationId);
      }

      const data = await jobsApi.create(jobData);
      console.log('[JobCreate] Job created successfully:', data.job.id);

      navigate(`/jobs/${data.job.id}`);
    } catch (error: any) {
      console.error('[JobCreate] Error creating job:', error);
      const errorMessage = error?.message || 'Unknown error occurred';
      alert(`Error creating job posting: ${errorMessage}`);
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
      console.log('[JobCreate] Submission complete');
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleConfigChange = (field: string, value: string | number | boolean) => {
    setInterviewConfig((prev) => ({ ...prev, [field]: value }));
  };

  const addQuestion = () => {
    setQuestions([...questions, { question: '', expected_answer: '', keywords: '' }]);
  };

  const removeQuestion = (index: number) => {
    if (questions.length > 1) {
      setQuestions(questions.filter((_, i) => i !== index));
    }
  };

  const updateQuestion = (index: number, field: keyof Question, value: string) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/jobs')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Jobs
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-3xl">Create Job Posting</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="title">Job Title *</Label>
                  <Input
                    id="title"
                    required
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="e.g. Senior Frontend Developer"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    placeholder="e.g. TechCorp Inc. (optional)"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Job Description *</Label>
                <Textarea
                  id="description"
                  required
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Describe the role, responsibilities, and what the candidate will be doing..."
                  rows={6}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="requirements">Requirements *</Label>
                <Textarea
                  id="requirements"
                  required
                  value={formData.requirements}
                  onChange={(e) => handleChange('requirements', e.target.value)}
                  placeholder="List the qualifications, experience, and skills required..."
                  rows={5}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tech_stack">Tech Stack</Label>
                <Input
                  id="tech_stack"
                  value={formData.tech_stack}
                  onChange={(e) => handleChange('tech_stack', e.target.value)}
                  placeholder="e.g. React, TypeScript, Node.js, PostgreSQL"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    placeholder="e.g. San Francisco, CA / Remote"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="salary_range">Salary Range</Label>
                  <Input
                    id="salary_range"
                    value={formData.salary_range}
                    onChange={(e) => handleChange('salary_range', e.target.value)}
                    placeholder="e.g. $120k - $180k"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="employment_type">Employment Type</Label>
                <Select
                  value={formData.employment_type}
                  onValueChange={(value) => handleChange('employment_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full-time">Full-time</SelectItem>
                    <SelectItem value="part-time">Part-time</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="posted_by">Posted By</Label>
                <Input
                  id="posted_by"
                  value={formData.posted_by}
                  onChange={(e) => handleChange('posted_by', e.target.value)}
                  placeholder="Your name or email"
                />
              </div>

              <div className="border-t pt-6 mt-6">
                <h3 className="text-xl font-semibold mb-4">Interview Configuration</h3>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-4">
                    <Button
                      type="button"
                      variant={useExistingConfig ? 'default' : 'outline'}
                      onClick={() => setUseExistingConfig(true)}
                    >
                      Use Existing
                    </Button>
                    <Button
                      type="button"
                      variant={!useExistingConfig ? 'default' : 'outline'}
                      onClick={() => setUseExistingConfig(false)}
                    >
                      Create New
                    </Button>
                  </div>

                  {useExistingConfig ? (
                    <div className="space-y-2">
                      <Label>Select Interview Configuration</Label>
                      <Select
                        value={formData.interview_config_id}
                        onValueChange={(value) => handleChange('interview_config_id', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select interview type" />
                        </SelectTrigger>
                        <SelectContent>
                          {configs.map((config) => (
                            <SelectItem key={config.id} value={config.id}>
                              {config.name} ({config.interview_type} - {config.difficulty_level})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                      <div className="space-y-2">
                        <Label>Configuration Name (Optional)</Label>
                        <Input
                          value={interviewConfig.name}
                          onChange={(e) => handleConfigChange('name', e.target.value)}
                          placeholder="Leave blank to auto-generate"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Interviewer Personality *</Label>
                        <Textarea
                          required={!useExistingConfig}
                          value={interviewConfig.personality}
                          onChange={(e) => handleConfigChange('personality', e.target.value)}
                          placeholder="e.g. Warm and friendly, Professional and encouraging..."
                          rows={2}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Interview Rules *</Label>
                        <Textarea
                          required={!useExistingConfig}
                          value={interviewConfig.interview_rules}
                          onChange={(e) => handleConfigChange('interview_rules', e.target.value)}
                          placeholder="e.g. Ask questions one at a time. Listen carefully. Focus on practical experience..."
                          rows={3}
                        />
                      </div>

                      <div>
                        <Label className="mb-3 block">Interview Type</Label>
                        <div className="grid grid-cols-3 gap-3 mb-4">
                          <button
                            type="button"
                            onClick={() => handleConfigChange('interview_type', 'technical')}
                            className={`p-4 border-2 rounded-lg transition-all ${
                              interviewConfig.interview_type === 'technical'
                                ? 'border-blue-600 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="font-medium text-gray-900">Technical</div>
                            <div className="text-xs text-gray-600 mt-1">Skills & knowledge</div>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleConfigChange('interview_type', 'behavioral')}
                            className={`p-4 border-2 rounded-lg transition-all ${
                              interviewConfig.interview_type === 'behavioral'
                                ? 'border-blue-600 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="font-medium text-gray-900">Behavioral</div>
                            <div className="text-xs text-gray-600 mt-1">Situational & teamwork</div>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleConfigChange('interview_type', 'mixed')}
                            className={`p-4 border-2 rounded-lg transition-all ${
                              interviewConfig.interview_type === 'mixed'
                                ? 'border-blue-600 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="font-medium text-gray-900">Mixed</div>
                            <div className="text-xs text-gray-600 mt-1">Both types</div>
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Interviewer Voice</Label>
                          <Select
                            value={interviewConfig.voice_gender}
                            onValueChange={(value) => handleConfigChange('voice_gender', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="male">Male</SelectItem>
                              <SelectItem value="female">Female</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Difficulty Level</Label>
                          <Select
                            value={interviewConfig.difficulty_level}
                            onValueChange={(value) => handleConfigChange('difficulty_level', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="entry">Entry</SelectItem>
                              <SelectItem value="mid">Mid</SelectItem>
                              <SelectItem value="senior">Senior</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Duration (minutes)</Label>
                          <Input
                            type="number"
                            min="2"
                            max="60"
                            value={interviewConfig.max_duration_minutes}
                            onChange={(e) => handleConfigChange('max_duration_minutes', parseInt(e.target.value))}
                          />
                          <p className="text-sm text-gray-600">
                            Tokens required: {calculateTokensForDuration(interviewConfig.max_duration_minutes).toLocaleString()}
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label>Max Questions</Label>
                          <Input
                            type="number"
                            min="3"
                            max="20"
                            value={interviewConfig.max_questions}
                            onChange={(e) => handleConfigChange('max_questions', parseInt(e.target.value))}
                          />
                        </div>
                      </div>

                      <div className="space-y-3 mt-6">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={interviewConfig.randomize_questions}
                            onChange={(e) => handleConfigChange('randomize_questions', e.target.checked)}
                            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-700">
                              Randomize question order
                            </span>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Questions will be asked in a random order during the interview
                            </p>
                          </div>
                        </label>
                      </div>

                      <div className="space-y-4 mt-6">
                        <div className="flex items-center justify-between">
                          <Label className="text-lg">Interview Questions</Label>
                          <Button
                            type="button"
                            onClick={addQuestion}
                            size="sm"
                            variant="outline"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Question
                          </Button>
                        </div>

                        {questions.map((q, index) => (
                          <Card key={index} className="bg-white">
                            <CardContent className="p-4">
                              <div className="space-y-3">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 space-y-2">
                                    <Label>Question {index + 1}</Label>
                                    <Textarea
                                      value={q.question}
                                      onChange={(e) => updateQuestion(index, 'question', e.target.value)}
                                      placeholder="Enter the interview question..."
                                      rows={2}
                                    />
                                  </div>
                                  {questions.length > 1 && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeQuestion(index)}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>

                                <div className="space-y-2">
                                  <Label>Expected Answer / Context</Label>
                                  <Textarea
                                    value={q.expected_answer}
                                    onChange={(e) => updateQuestion(index, 'expected_answer', e.target.value)}
                                    placeholder="What kind of answer are you looking for? (Optional)"
                                    rows={2}
                                  />
                                </div>

                                <div className="space-y-2">
                                  <Label>Keywords (comma-separated)</Label>
                                  <Input
                                    value={q.keywords}
                                    onChange={(e) => updateQuestion(index, 'keywords', e.target.value)}
                                    placeholder="e.g. leadership, teamwork, problem-solving"
                                  />
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? 'Creating...' : 'Create Job Posting'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/jobs')}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
